import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Typography,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { membersAPI } from '../services/api';

const MemberForm = ({ open, onClose, onSuccess, member }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    photo: '',
    KOALM_number: '',
    IOA_LM_number: '',
    working_hospital_name: '',
    working_hospital_district: '',
    designation: '',
    communication_address: '',
    address1_place_post: '',
    district: '',
    pincode: '',
    state: '',
    district_club_name: '',
    mobile_number: '',
    whatsapp_number: '',
    landline_hospital: '',
    landline_residence: '',
    date_of_birth: '',
    blood_group: '',
    tcmc_reg_no: '',
    ima_life_membership_status: '',
    mbbs_college: '',
    mbbs_year: '',
    pg_diploma_college: '',
    pg_diploma_year: '',
    pg_degree_college: '',
    pg_degree_year: '',
    awards_honours: '',
    spouse_name: '',
  });

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name || '',
        email: member.email || '',
        photo: member.photo || '',
        KOALM_number: member.KOALM_number || '',
        IOA_LM_number: member.IOA_LM_number || '',
        working_hospital_name: member.working_hospital_name || '',
        working_hospital_district: member.working_hospital_district || '',
        designation: member.designation || '',
        communication_address: member.communication_address || '',
        address1_place_post: member.address1_place_post || '',
        district: member.district || '',
        pincode: member.pincode || '',
        state: member.state || '',
        district_club_name: member.district_club_name || '',
        mobile_number: member.mobile_number || '',
        whatsapp_number: member.whatsapp_number || '',
        landline_hospital: member.landline_hospital || '',
        landline_residence: member.landline_residence || '',
        date_of_birth: member.date_of_birth || '',
        blood_group: member.blood_group || '',
        tcmc_reg_no: member.tcmc_reg_no || '',
        ima_life_membership_status: member.ima_life_membership_status || '',
        mbbs_college: member.mbbs_college || '',
        mbbs_year: member.mbbs_year || '',
        pg_diploma_college: member.pg_diploma_college || '',
        pg_diploma_year: member.pg_diploma_year || '',
        pg_degree_college: member.pg_degree_college || '',
        pg_degree_year: member.pg_degree_year || '',
        awards_honours: member.awards_honours || '',
        spouse_name: member.spouse_name || '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        photo: '',
        KOALM_number: '',
        IOA_LM_number: '',
        working_hospital_name: '',
        working_hospital_district: '',
        designation: '',
        communication_address: '',
        address1_place_post: '',
        district: '',
        pincode: '',
        state: '',
        district_club_name: '',
        mobile_number: '',
        whatsapp_number: '',
        landline_hospital: '',
        landline_residence: '',
        date_of_birth: '',
        blood_group: '',
        tcmc_reg_no: '',
        ima_life_membership_status: '',
        mbbs_college: '',
        mbbs_year: '',
        pg_diploma_college: '',
        pg_diploma_year: '',
        pg_degree_college: '',
        pg_degree_year: '',
        awards_honours: '',
        spouse_name: '',
      });
    }
  }, [member, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (member) {
        await membersAPI.update(member.id, formData);
      } else {
        await membersAPI.create(formData);
      }
      onSuccess();
    } catch (error) {
      alert('Failed to save member. Please try again.');
      console.error('Error saving member:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {member ? 'Edit Member' : 'Add New Member'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Id *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="KOA LM No *"
                  name="KOALM_number"
                  value={formData.KOALM_number}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IOA LM No"
                  name="IOA_LM_number"
                  value={formData.IOA_LM_number}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth *"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Blood Group *"
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name of Spouse"
                  name="spouse_name"
                  value={formData.spouse_name}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Professional Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Professional Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Working Hospital Name & District *"
                  name="working_hospital_name"
                  value={formData.working_hospital_name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Working Hospital District"
                  name="working_hospital_district"
                  value={formData.working_hospital_district}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Designation *"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="TCMC Reg No *"
                  name="tcmc_reg_no"
                  value={formData.tcmc_reg_no}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">IMA Life Membership Status</FormLabel>
                  <RadioGroup
                    row
                    name="ima_life_membership_status"
                    value={formData.ima_life_membership_status || ''}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Education */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Education
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="MBBS (College & Year)"
                  name="mbbs_college"
                  value={formData.mbbs_college}
                  onChange={handleChange}
                  placeholder="College name"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="MBBS Year"
                  name="mbbs_year"
                  value={formData.mbbs_year}
                  onChange={handleChange}
                  placeholder="Year"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PG Diploma (College & Year)"
                  name="pg_diploma_college"
                  value={formData.pg_diploma_college}
                  onChange={handleChange}
                  placeholder="College name"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PG Diploma Year"
                  name="pg_diploma_year"
                  value={formData.pg_diploma_year}
                  onChange={handleChange}
                  placeholder="Year"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PG Degree (College & Year)"
                  name="pg_degree_college"
                  value={formData.pg_degree_college}
                  onChange={handleChange}
                  placeholder="College name"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PG Degree Year"
                  name="pg_degree_year"
                  value={formData.pg_degree_year}
                  onChange={handleChange}
                  placeholder="Year"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Awards / Honours"
                  name="awards_honours"
                  value={formData.awards_honours}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile No *"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="WhatsApp No (Omit if same as Mobile)"
                  name="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Land Line No (Hospital)"
                  name="landline_hospital"
                  value={formData.landline_hospital}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Land Line (Residence)"
                  name="landline_residence"
                  value={formData.landline_residence}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Address Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Address Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Communication Address (House Name / No) *"
                  name="communication_address"
                  value={formData.communication_address}
                  onChange={handleChange}
                  required
                  multiline
                  rows={2}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address1 (Place & Post office) *"
                  name="address1_place_post"
                  value={formData.address1_place_post}
                  onChange={handleChange}
                  required
                  multiline
                  rows={2}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="District *"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode *"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="District Club Name *"
                  name="district_club_name"
                  value={formData.district_club_name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Saving...' : member ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MemberForm;
